import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronUp, ChevronDown, Loader2, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { useEarlyAccessFilters } from '../hooks/useEarlyAccessFilters';
import { useEarlyAccessSignups } from '../hooks/useEarlyAccessSignups';
import { SearchBar } from '../components/SearchBar';
import { Pagination } from '../components/Pagination';
import type { EarlyAccessFilters } from '../types/early-access';

function SortableHeader({
  label,
  sortOrder,
  onSort,
}: {
  label: string;
  sortOrder: EarlyAccessFilters['sortOrder'];
  onSort: () => void;
}) {
  return (
    <th className="text-left px-6 py-4 text-body-sm font-medium text-pine-ink-soft uppercase tracking-wider">
      <button
        onClick={onSort}
        className="flex items-center gap-2 hover:text-pine-pine transition-colors group"
      >
        <span>{label}</span>
        <div className="flex flex-col">
          <ChevronUp
            className={`w-3 h-3 -mb-1 transition-colors ${
              sortOrder === 'asc'
                ? 'text-pine-pine'
                : 'text-pine-ink-soft/40 group-hover:text-pine-ink-soft'
            }`}
          />
          <ChevronDown
            className={`w-3 h-3 -mt-1 transition-colors ${
              sortOrder === 'desc'
                ? 'text-pine-pine'
                : 'text-pine-ink-soft/40 group-hover:text-pine-ink-soft'
            }`}
          />
        </div>
      </button>
    </th>
  );
}

export default function EarlyAccessSignupsPage() {
  const { filters, updateFilter } = useEarlyAccessFilters();
  const { data, isLoading, isError, error } = useEarlyAccessSignups(filters);

  useEffect(() => {
    document.title = 'Early Access Signups | SparkAdmin';
  }, []);

  useEffect(() => {
    if (isError && error) {
      toast.error(error.message || 'Failed to load signups');
    }
  }, [isError, error]);

  const handleSort = () => {
    updateFilter('sortOrder', filters.sortOrder === 'asc' ? 'desc' : 'asc');
  };

  const totalPages = data ? Math.ceil(data.total / data.pageSize) : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-6 space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-heading-lg font-bold text-pine-ink">
            Early Access Signups
          </h1>
          <p className="text-body-sm text-pine-ink-soft mt-1">
            {data ? `${data.total} total signups` : 'Loading...'}
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="max-w-md">
        <SearchBar
          value={filters.search}
          onChange={(value) => updateFilter('search', value)}
          placeholder="Search by email..."
        />
      </div>

      {/* Table */}
      <div className="bg-pine-card rounded-xl border border-pine-line overflow-hidden shadow-pine-card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-pine-paper/50 border-b border-pine-line">
              <tr>
                <th className="text-left px-6 py-4 text-body-sm font-medium text-pine-ink-soft uppercase tracking-wider">
                  Email
                </th>
                <SortableHeader
                  label="Signed Up"
                  sortOrder={filters.sortOrder}
                  onSort={handleSort}
                />
              </tr>
            </thead>
            <tbody className="divide-y divide-pine-line">
              {isLoading ? (
                <tr>
                  <td colSpan={2} className="px-6 py-12 text-center">
                    <Loader2 className="w-6 h-6 animate-spin text-pine-pine mx-auto" />
                  </td>
                </tr>
              ) : !data?.items.length ? (
                <tr>
                  <td colSpan={2} className="px-6 py-12 text-center text-pine-ink-soft">
                    <Mail className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No signups found</p>
                  </td>
                </tr>
              ) : (
                data.items.map((signup) => (
                  <tr
                    key={signup.id}
                    className="hover:bg-pine-hover-item transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className="text-body-sm text-pine-ink">
                          {signup.email}
                        </span>
                        {signup.duplicateCount > 1 && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500/20 text-amber-400">
                            ×{signup.duplicateCount}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-body-sm text-pine-ink-soft">
                      {new Date(signup.createdAt).toLocaleString()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {data && totalPages > 1 && (
        <Pagination
          currentPage={filters.page}
          totalPages={totalPages}
          totalItems={data.total}
          pageSize={filters.pageSize}
          onPageChange={(page) => updateFilter('page', page)}
        />
      )}
    </motion.div>
  );
}
